import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { parsePhoneNumber } from 'react-phone-number-input'

import { createLeadContact, deleteContactInfoApi, updateLeadContact } from 'services/lead-contact'
import useLeadContact, { useLeadContactQueryClient } from 'queries/lead-contact'
import { Contact, SelectValue } from 'modules/Leads/LeadDetail/shared/ContactPersonDropdown'
import { ContactInfo } from 'store/types/lead'
import { LeadDetails, PhoneNumber } from 'types/global'
import { formatContactNumberDisplayValue } from 'utils/generic'

import useBranch from './use-branch'
import useWorkFlowLookup from './lookup/use-workflow-lookup'
import { AppQueryOptions } from './use-app-query'

/**
 * Exported separately due to implementation in Activities tab (virtualized list)
 *  causing re-rendering when used with `useBranch` hook
 */
export const resolveContactNumber = (contactInfoId: string, list: LeadDetails.ContactPerson[]) => {
  for (const contactPerson of list as LeadDetails.ContactPerson[]) {
    const contact = contactPerson.mobile.find((mobile: LeadDetails.Mobile) => mobile.id === contactInfoId)

    if (contact?.value) {
      return contact.displayValue
    }
  }

  return ''
}

export const generateTempContact = (
  { countryCode, number }: PhoneNumber,
  contactInfoTypeId: string,
  leadContact?: LeadDetails.ContactPerson,
): Contact => {
  const tempNumber = {
    contactInfoId: `id-new-number-number-${number}`,
    contactPersonId: `id-new-number-contact-${number}`,
    contactTypeId: contactInfoTypeId,
    displayValue: formatContactNumberDisplayValue(countryCode, number),
    value: number?.toString() || '',
    isPrimary: false,
  }

  return {
    contactPersonId: leadContact?.id || tempNumber.contactPersonId,
    fname: leadContact?.fname,
    lname: leadContact?.lname,
    numbers: [tempNumber],
    isPrimary: leadContact?.isPrimary || false,
  }
}

interface Props {
  leadId?: string
  queryOptions?: AppQueryOptions
}

const useContactInfo = (props?: Props) => {
  const { countryCode } = useBranch()
  const { leadId: leadIdParam = '' } = useParams<{ leadId: string }>()
  const leadId = props?.leadId || leadIdParam
  const { contactInfoTypes, mobileContactInfoTypeOptions } = useWorkFlowLookup({})
  const { invalidateQuery } = useLeadContactQueryClient(leadId)
  const queryResult = useLeadContact(leadId, props?.queryOptions)

  const mobileContactInfos: LeadDetails.ContactPerson[] = useMemo(
    () => queryResult.data?.filter((i) => i.isActive && i.mobile.length > 0) ?? [],
    [queryResult.data],
  )

  const createContact = async (value: LeadDetails.ContactPerson) =>
    await createLeadContact({
      contact_type_id: value.contactTypeId,
      email: value.email
        ?.filter((i) => i.value)
        .map((i) => ({
          contact_info_id: i.id,
          contact_info_type_id: i.contactInfoTypeId,
          is_primary: i.isPrimary,
          value: i.value,
        })),
      fname: value.fname,
      is_primary: value.isPrimary,
      job_title: value.jobTitle,
      lname: value.lname,
      lead_id: value.leadId,
      mobile: value.mobile
        ?.filter((i) => i.value)
        .map((i) => ({
          contact_info_id: i.id,
          contact_info_type_id: i.contactInfoTypeId,
          is_primary: i.isPrimary,
          value: i.value,
        })),
    })

  const formatContactNumberDropdownValue = useCallback(
    (contactPerson?: LeadDetails.ContactPerson, mobileContactInfo?: LeadDetails.Mobile): SelectValue | undefined => {
      if (contactPerson && mobileContactInfo?.id) {
        return {
          contactInfoId: mobileContactInfo.id,
          contactPersonId: contactPerson.id,
          contactInfoTypeId: mobileContactInfo.contactInfoTypeId,
          displayValue: mobileContactInfo.displayValue,
          fname: contactPerson.fname,
          isMobilePrimary: mobileContactInfo.isPrimary,
          lname: contactPerson.lname,
          value: mobileContactInfo.value,
        }
      }
    },
    [],
  )

  const getContactPersonByNumber = useCallback(
    (contactNumber: string, list?: LeadDetails.ContactPerson[]) => {
      const parsed = parsePhoneNumber(contactNumber)

      let contactPerson: LeadDetails.ContactPerson | undefined = undefined
      let mobileContactInfo: LeadDetails.Mobile | undefined = undefined

      const source = list || queryResult.data || []

      if (parsed) {
        contactPerson = source.find((i: LeadDetails.ContactPerson) =>
          i.mobile.find((o) => o.id && o.value === parsed.number),
        )
        mobileContactInfo = contactPerson?.mobile.find((i) => i.value === parsed.number)
      }

      return { contactPerson, mobileContactInfo }
    },
    [queryResult.data],
  )

  const getContactInfoById = useCallback(
    (contactInfoId: string) => {
      let contactPerson: LeadDetails.ContactPerson | undefined = undefined
      let mobileContactInfo: LeadDetails.Mobile | undefined = undefined

      if (queryResult.data) {
        contactPerson = queryResult.data.find((i) => i.mobile.find((o) => o.id === contactInfoId))
        mobileContactInfo = contactPerson?.mobile.find((i) => i.id === contactInfoId)
      }

      return { contactPerson, mobileContactInfo }
    },
    [queryResult.data],
  )

  const getPrimaryContactInfo = useCallback(() => {
    let contactPerson: LeadDetails.ContactPerson | undefined = undefined
    let mobileContactInfo: LeadDetails.Mobile | undefined = undefined

    if (queryResult.data) {
      contactPerson = queryResult.data.find((i) => i.isPrimary)
      mobileContactInfo = contactPerson?.mobile.find((i) => i.isPrimary)
    }

    return { contactPerson, mobileContactInfo }
  }, [queryResult.data])

  const primaryContactInfo = useMemo(() => {
    let contactPerson: LeadDetails.ContactPerson | undefined = undefined
    let mobileContactInfo: LeadDetails.Mobile | undefined = undefined

    if (queryResult.data) {
      contactPerson = queryResult.data.find((i) => i.isPrimary)
      mobileContactInfo = contactPerson?.mobile.find((i) => i.isPrimary)
    }

    return {
      contactInfoId: mobileContactInfo?.id || '',
      fname: contactPerson?.fname,
      lname: contactPerson?.lname,
      contactInfoTypeId: mobileContactInfo?.contactInfoTypeId || '',
      contactValue: mobileContactInfo?.value || '',
      isOtherNumber: false,
      isNewNumber: false,
      isPrimaryContact: contactPerson?.isPrimary || false,
      otherContactInfoTypeId: undefined,
      otherContactValue: undefined,
    }
  }, [queryResult.data])

  const getContactNumberOptions = useCallback(
    (otherNumber?: ContactInfo) => {
      if (!queryResult.data) {
        return []
      }

      const contacts: Contact[] = []

      queryResult.data
        .filter((i) => i.isActive)
        .forEach((contact) => {
          const { id: contactPersonId, fname, lname, isPrimary, mobile } = contact

          if (mobile.length > 0) {
            contacts.push({
              fname,
              contactPersonId,
              isPrimary,
              lname,
              numbers: mobile.map((n: LeadDetails.Mobile) => ({
                contactInfoId: n.id ?? '',
                isPrimary: n.isPrimary,
                contactTypeId: n.contactInfoTypeId,
                contactType: mobileContactInfoTypeOptions.find((i) => i.value === n.contactInfoTypeId)?.label || '',
                displayValue: n.displayValue,
                value: n.value,
              })),
            })
          }
        })

      // Add other number to options if available
      if (otherNumber?.otherContactValue && otherNumber?.otherContactInfoTypeId) {
        const contactInfoType = mobileContactInfoTypeOptions.find((i) => i.value === otherNumber.otherContactInfoTypeId)
        const parsed = parsePhoneNumber(otherNumber.otherContactValue)

        if (contactInfoType && parsed?.countryCallingCode && parsed.nationalNumber) {
          contacts.push(
            generateTempContact(
              { countryCode: Number(parsed.countryCallingCode), number: parsed.nationalNumber },
              otherNumber.otherContactInfoTypeId,
            ),
          )
        }
      }

      return contacts
    },
    [queryResult.data, mobileContactInfoTypeOptions],
  )

  const getInitialEmail = useCallback(
    () => ({
      contactInfoTypeId: contactInfoTypes.find((i) => i.name.toLowerCase() === 'email')?.id || '',
      value: '',
      isPrimary: true,
    }),
    [contactInfoTypes],
  )

  const getInitialMobileNumber = useCallback(
    () => ({
      contactInfoId: '',
      contactInfoTypeId: contactInfoTypes.find((item) => item.isDefault)?.id || '',
      countryISO: countryCode,
      displayValue: '',
      isPrimary: true,
      value: '',
    }),
    [contactInfoTypes, countryCode],
  )

  const getInitialLeadContact = useCallback(
    (options: { hasEmail?: boolean; hasMobile?: boolean } = {}): LeadDetails.ContactPerson => ({
      id: '',
      contactTypeId: '',
      email: options.hasEmail ? [getInitialEmail()] : [],
      fname: '',
      jobTitle: '',
      leadId: leadId || '',
      isActive: true,
      isPrimary: false,
      lname: '',
      mobile: options.hasMobile ? [getInitialMobileNumber()] : [],
    }),
    [getInitialEmail, getInitialMobileNumber, leadId],
  )

  const updateContact = async (value: LeadDetails.ContactPerson) =>
    await updateLeadContact({
      contact_id: value.id,
      contact_type_id: value.contactTypeId,
      email: value.email
        ?.filter((i) => i.value)
        .map((i) => ({
          contact_info_id: i.id,
          contact_info_type_id: i.contactInfoTypeId,
          is_primary: i.isPrimary,
          value: i.value,
        })),
      fname: value.fname,
      is_primary: value.isPrimary,
      job_title: value.jobTitle,
      lname: value.lname,
      lead_id: value.leadId,
      mobile: value.mobile
        ?.filter((i) => i.value || i.number)
        .map((i) => ({
          contact_info_id: i.id,
          contact_info_type_id: i.contactInfoTypeId,
          is_primary: i.isPrimary,
          value: i.value || i.number?.toString() || '',
        })),
    })

  const deleteContactInfo = async (contactInfoId: string, contactPersonId: string) =>
    await deleteContactInfoApi({
      contact_info_id: contactInfoId,
      contact_person_id: contactPersonId,
    })

  return {
    ...queryResult,
    primaryContactInfo,
    invalidateQuery,
    mobileContactInfos,
    createContact,
    deleteContactInfo,
    formatContactNumberDropdownValue,
    getContactInfoById,
    getContactNumberOptions,
    getContactPersonByNumber,
    getInitialEmail,
    getInitialLeadContact,
    getInitialMobileNumber,
    getPrimaryContactInfo,
    updateContact,
  }
}

export default useContactInfo
